import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useProfileStore, getFacilityKeyForPortal } from "@/store/profile";
import { useTaskStore } from "@/store/task";
import { useHealthHistoryStore, CONDITION_LABELS, conditionDisplayLabel } from "@/store/healthHistory";
import { MEDICINE_QUESTIONS, MEDICINE2_QUESTIONS, INFO_QUESTIONS } from "@/store/healthHistory";
import taskRequest from "@/api/task-request";
import { complaintHistoryLabels as t } from "@/components/complaints/complaintHistoryLabels";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { ChoiceGroup } from "@/components/forms/ChoiceGroup";

export function HealthHistory1Page() {
  const navigate = useNavigate();
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const caseHistoryId = useTaskStore((s) => s.caseHistory?.id);
  const facilityKey = getFacilityKeyForPortal();
  const isFemale = (currentPatient?.sex ?? "").toUpperCase() === "F";

  const conditions = useHealthHistoryStore((s) => s.conditions);
  const setConditions = useHealthHistoryStore((s) => s.setConditions);
  const setInfoAnswers = useHealthHistoryStore((s) => s.setInfoAnswers);
  const setMedicineAnswers = useHealthHistoryStore((s) => s.setMedicineAnswers);
  const setMedicine2Answers = useHealthHistoryStore((s) => s.setMedicine2Answers);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!facilityKey || !caseHistoryId) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    Promise.all([
      taskRequest.request.getCaseHistoryQuestions(facilityKey, { caseHistoryId }),
      taskRequest.request.getCaseHistoryConditions(facilityKey, caseHistoryId),
    ])
      .then(([qRes, cRes]) => {
        if (cancelled) return;
        const questions = (qRes.data as { description?: string; answer?: string }[]) ?? [];
        const medicineCodes = [...MEDICINE_QUESTIONS.map((m) => m.code)];
        if (isFemale) medicineCodes.push("BirthControl");
        const medicine2Codes = [...MEDICINE2_QUESTIONS.map((m) => m.code)];
        if (isFemale) {
          medicine2Codes.push("Pregnant", "LastMenstrualPeriod");
        }
        questions.forEach((el) => {
          if (!el.description || el.answer === undefined || el.answer === "") return;
          const code = el.description;
          if (INFO_QUESTIONS.some((i) => i.code === code)) {
            setInfoAnswers((prev) => ({ ...prev, [code]: el.answer ?? "" }));
          } else if (medicineCodes.includes(code)) {
            const parts = (el.answer ?? "").split(",");
            setMedicineAnswers((prev) => ({
              ...prev,
              [code]: { answer: parts[0] ?? "", year: parts[1] ?? "" },
            }));
          } else if (medicine2Codes.includes(code)) {
            setMedicine2Answers((prev) => ({ ...prev, [code]: el.answer ?? "" }));
          }
        });
        const condData = (cRes.data as { description?: string; present?: boolean; past?: boolean }[]) ?? [];
        condData.forEach((el) => {
          if (el.description && CONDITION_LABELS.includes(el.description)) {
            setConditions((prev) => ({
              ...prev,
              [el.description!]: { present: !!el.present, past: !!el.past },
            }));
          }
        });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [facilityKey, caseHistoryId, isFemale, setInfoAnswers, setConditions, setMedicineAnswers, setMedicine2Answers]);

  const toggleCondition = (label: string, field: "present" | "past") => {
    setConditions((prev) => ({
      ...prev,
      [label]: { ...(prev[label] ?? { present: false, past: false }), [field]: !prev[label]?.[field] },
    }));
  };

  const goBack = () => navigate("/complaint/primary-complaint");
  const goNext = () => navigate("/complaint/health-history-2");

  if (!currentPatient) {
    return (
      <div className="wizard-page w-full">
        <div className="wizard-page__content px-4 pt-4 pb-0">
          <p className="text-sm text-slate-600">Please sign in or select a patient.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content px-4 pt-4 pb-0">
        <div className="complaint-history-card">
          <div className="complaint-history-heading">
            <div className="complaint-history-heading-bar" />
            <h2 className="complaint-history-heading-text">
              Please indicate the following condition(s) that apply to you
            </h2>
          </div>
          <div className="complaint-history-divider" />

          {isLoading ? (
            <div className="py-8">
              <div className="complaint-history-progress-track">
                <div className="complaint-history-progress-fill w-1/3" />
              </div>
            </div>
          ) : (
            <>
              <div className="complaint-history-section-block history-choice-rows grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {CONDITION_LABELS.map((label) => {
                  const selected = (["present", "past"] as const).filter((f) => conditions[label]?.[f]);
                  return (
                    <div key={label} className="complaint-history-med-card history-choice-row">
                      <ChoiceGroup
                        id={`condition-${label.replace(/[^a-zA-Z0-9]+/g, "-")}`}
                        multiple
                        layout="segmented"
                        legend={conditionDisplayLabel(label)}
                        options={[
                          { value: "present", label: "Present" },
                          { value: "past", label: "Past" },
                        ]}
                        value={selected}
                        onChange={(next) => {
                          // ChoiceGroup reports the whole new list; toggle the one choice that changed.
                          const toggled =
                            next.find((f) => !selected.includes(f as "present" | "past")) ??
                            selected.find((f) => !next.includes(f));
                          if (toggled) toggleCondition(label, toggled as "present" | "past");
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack}>
          {t.backButton}
        </Button>
        <Button type="button" onClick={goNext} disabled={isLoading}>
          {t.continueButton}
        </Button>
      </WizardActionsBar>
    </div>
  );
}
